import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, ShieldCheck } from 'lucide-react';
import { useAuth, api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import WaitlistStaffPanel from './WaitlistStaffPanel';

/** A gestão da lista é exclusiva de staff/admin. A API reforça as mesmas permissões. */
export default function WaitlistManagementPage() {
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const permitted = isAdmin || user?.role === 'staff';
  const [staffId, setStaffId] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');
  useEffect(() => {
    if (authLoading) return;
    if (!permitted) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (isAdmin) {
      setLoadingProfile(false);
      return;
    }
    let active = true;
    setLoadingProfile(true);
    api.get('/staff').then(response => {
      if (!active) return;
      const list = Array.isArray(response.data) ? response.data : [];
      const email = String(user?.email || '').trim().toLowerCase();
      const mine = list.find(p => p.user_id && p.user_id === user?.user_id) ||
        list.find(p => String(p.email || '').trim().toLowerCase() === email);
      if (mine?.staff_id) setStaffId(mine.staff_id);
      else setProfileError(language === 'pt' ? 'O teu utilizador não está associado a um perfil de profissional. Pede à administração para verificar essa associação.' : 'Your account is not linked to a staff profile. Please contact the administrator.');
    }).catch(() => {
      if (active) setProfileError(language === 'pt' ? 'Não foi possível consultar o teu perfil de profissional.' : 'Unable to load your staff profile.');
    }).finally(() => { if (active) setLoadingProfile(false); });
    return () => { active = false; };
  }, [authLoading, isAdmin, permitted, navigate, user?.email, user?.user_id, language]);

  if (authLoading || !user || !permitted || loadingProfile) {
    return <main className="min-h-[100svh] bg-[#F9F8F6] px-4 pt-28 sm:px-6 sm:pt-32"><p className="text-center text-[#635E54]">{language === 'pt' ? 'A carregar…' : 'Loading…'}</p></main>;
  }

  return (
    <main className="min-h-[100svh] min-w-0 overflow-x-clip bg-[#F9F8F6] pt-24 sm:pt-28" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }} data-testid="waitlist-management-page">
      <div className="mx-auto w-full min-w-0 max-w-7xl px-4 sm:px-6 md:px-10 lg:px-16">
        <button type="button" onClick={() => navigate('/schedule')} className="mb-4 inline-flex min-h-[44px] items-center gap-2 text-sm text-[#635E54] hover:text-[#BFA05F] sm:mb-5">
          <ArrowLeft className="h-4 w-4" />{language === 'pt' ? 'Voltar à agenda' : 'Back to schedule'}
        </button>
        <div className="mb-6 flex min-w-0 flex-col gap-4 sm:mb-8 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.18em] text-[#BFA05F]">
              <ClipboardList className="h-4 w-4" /> Lou's Way · {language === 'pt' ? 'Gestão' : 'Management'}
            </div>
            <h1 className="font-heading text-2xl font-medium leading-tight text-[#1A1815] sm:text-3xl md:text-4xl">{language === 'pt' ? 'Gestão da Lista de Espera' : 'Waitlist Management'}</h1>
            <p className="mt-2 max-w-2xl text-sm text-[#635E54]">{language === 'pt' ? 'Consulta pedidos, encontra vagas compatíveis e acompanha ofertas enviadas às clientes.' : 'Review requests, find matching available slots, and track offers to clients.'}</p>
          </div>
          <div className="flex min-w-0 items-center gap-2 self-start rounded-xl border border-[#E6E2DA] bg-white px-3 py-3 text-xs text-[#635E54] sm:px-4 sm:text-sm">
            <ShieldCheck className="h-4 w-4 text-[#BFA05F]" />
            {isAdmin ? (language === 'pt' ? 'Administração · todas as agendas' : 'Admin · all schedules') : (language === 'pt' ? 'Apenas a tua agenda' : 'Your schedule only')}
          </div>
        </div>
        {profileError ? (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800">{profileError}</div>
        ) : (
          <WaitlistStaffPanel language={language} myStaffId={staffId} isAdmin={isAdmin} />
        )}
      </div>
    </main>
  );
}
